import { isNativeApp, isIOS } from './capacitor';

/**
 * ネイティブアプリ初期化処理
 * application.tsxから呼び出す
 */
export async function initNativeApp(): Promise<void> {
  console.log(`[NativeBridge] isNativeApp: ${isNativeApp()}`);
  if (!isNativeApp()) return;

  // CSSでSafe Area対応するためにクラスを付与
  if (isIOS()) {
    document.documentElement.classList.add('capacitor-ios');
  }

  // ステータスバー設定（iOSのみ）
  if (isIOS()) {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
  }

  // スプラッシュスクリーン非表示
  const { SplashScreen } = await import('@capacitor/splash-screen');
  await SplashScreen.hide();

  // アプリのバックグラウンド/フォアグラウンド検出
  const { App } = await import('@capacitor/app');
  App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      console.log('[NativeBridge] App became active');
      // フォアグラウンド復帰時にローカル通知を再登録
      import('@/lib/local-notification-scheduler').then(({ rescheduleAllNotifications }) => {
        rescheduleAllNotifications();
      }).catch(console.error);
    }
  });

  // ディープリンク処理（認証コールバックは各認証モジュールが処理するためスキップ）
  App.addListener('appUrlOpen', ({ url }) => {
    console.log('[NativeBridge] Deep link received:', url);
    const parsed = new URL(url);
    // ukarte://auth/* は native-line-auth.ts 等が処理する
    if (parsed.host === 'auth') return;
    const path = parsed.pathname;
    if (path) {
      window.location.pathname = path;
    }
  });

  // ハードウェアバックボタン（Androidのみだが安全のため登録）
  App.addListener('backButton', () => {
    window.history.back();
  });

  // ローカル通知タップ時のハンドリング
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  LocalNotifications.addListener('localNotificationActionPerformed', (notification) => {
    const extra = notification.notification.extra;
    if (extra?.type && extra?.id) {
      navigateToNotificationTarget(extra.type, extra.id, extra.scheduledAt);
    }
  });

  // アプリ起動時にローカル通知を一括再登録
  import('@/lib/local-notification-scheduler').then(({ rescheduleAllNotifications }) => {
    rescheduleAllNotifications();
  }).catch(console.error);

  console.log('[NativeBridge] Native app initialized');
}

/**
 * プッシュ通知の登録とデバイストークンのサーバー送信
 * ユーザーのログイン後に呼び出す
 */
export async function registerPushNotifications(): Promise<string | null> {
  if (!isNativeApp()) return null;

  const { PushNotifications } = await import('@capacitor/push-notifications');

  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') {
    console.log('[NativeBridge] Push notification permission denied');
    return null;
  }

  await PushNotifications.register();

  return new Promise((resolve) => {
    PushNotifications.addListener('registration', async (token) => {
      console.log('[NativeBridge] Push token:', token.value);
      // デバイストークンをサーバーに送信
      await sendDeviceTokenToServer(token.value);
      resolve(token.value);
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('[NativeBridge] Push registration error:', err);
      resolve(null);
    });

    // 通知タップ時のハンドリング
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      const data = notification.notification.data;
      if (data?.type && data?.id) {
        navigateToNotificationTarget(data.type, data.id, data.scheduledAt);
      }
    });
  });
}

/**
 * デバイストークンをサーバーに送信
 */
async function sendDeviceTokenToServer(token: string): Promise<void> {
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    const { Device } = await import('@capacitor/core').then(m => ({ Device: m.Capacitor }));

    await fetch('/api/v1/users/device_token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        token,
        platform: 'ios',
        device_name: `${Device.getPlatform()} device`,
      }),
    });
    console.log('[NativeBridge] Device token sent to server');
  } catch (err) {
    console.error('[NativeBridge] Failed to send device token:', err);
  }
}

/**
 * 通知タップ時に対応するページへ遷移
 */
function navigateToNotificationTarget(type: string, id: string | number, scheduledAt?: string): void {
  if (type === 'schedule') {
    // 日付付きでカレンダーの日ビューに遷移し、詳細モーダルを自動オープン
    const dateParam = scheduledAt ? new Date(scheduledAt).toISOString().split('T')[0] : '';
    const params = new URLSearchParams({ view: 'day', scheduleId: String(id) });
    if (dateParam) params.set('date', dateParam);
    window.location.href = `/users/schedules?${params.toString()}`;
    return;
  }

  const routes: Record<string, string> = {
    interview_practice: `/users/interview_practices/${id}`,
    interview_request: `/users/interview_requests`,
  };

  const path = routes[type];
  if (path) {
    window.location.pathname = path;
  }
}

/**
 * 触覚フィードバック（ボタンタップ時などに使用）
 */
export async function hapticFeedback(
  style: 'light' | 'medium' | 'heavy' = 'light'
): Promise<void> {
  if (!isNativeApp()) return;

  const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
  const styleMap = {
    light: ImpactStyle.Light,
    medium: ImpactStyle.Medium,
    heavy: ImpactStyle.Heavy,
  };
  await Haptics.impact({ style: styleMap[style] });
}
